import { useState } from 'react';
import { translate } from 'translations/i18n';
import { useMutation, gql } from '@apollo/client';
import toast from 'helpers/toast';
import { uploadFile } from 'helpers/direct-upload';
import errorLogger from 'helpers/error-logger';
import useDirectUpload from 'javascripts/hooks/useDirectUpload';
import { GET_DOCUMENT_BLUEPRINTS } from './useGetCompanyDocuments';
import Steven from 'helpers/steven';

const CREATE_DOCUMENT_BLUEPRINT = gql`
  mutation CreateDocumentBlueprint($input: CreateDocumentBlueprintInput!) {
    createDocumentBlueprint(input: $input) {
      errors {
        error
        message
      }
      documentBlueprint {
        __typename
        id
        databaseId
        name
        updatedAt
      }
    }
  }
`;

const useUploadCompanyDocuments = ({ onComplete = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { createDirectUpload } = useDirectUpload();
  const [createDocument] = useMutation(CREATE_DOCUMENT_BLUEPRINT, {
    refetchQueries: [
      {
        query: GET_DOCUMENT_BLUEPRINTS,
        variables: { first: 15 },
      },
    ],
  });

  const uploadCompanyDocument = async (file) => {
    try {
      const { data } = await createDirectUpload({
        file,
        className: 'DocumentBlueprint',
      });

      const {
        createDirectUpload: {
          directUpload: { url, headers, signedBlobId },
        },
      } = data;

      await uploadFile(url, headers, file);
      await createDocument({
        variables: {
          input: {
            name: file.name,
            signedBlobId,
          },
        },
      });

      setCompletedCount((prevCount) => prevCount + 1);
      Steven.entityCrud({
        entity_type: 'company_document',
        entity_id: data.createDirectUpload?.directUpload?.blobId,
        action: 'created'
      });
    } catch (e) {
      errorLogger(e);
      throw new Error(e);
    }
  };

  const uploadCompanyDocuments = async ({ files }) => {
    setLoading(true);
    setTotalCount(files.length);

    const promises = files.map((file, index) =>
      uploadCompanyDocument(file, index),
    );

    const results = await Promise.allSettled(promises);
    const rejected = results.flatMap((result, index) => {
      if (result.status === 'rejected') {
        return index;
      }
      return [];
    });

    if (rejected.length) {
      const rejectedFileName = files[rejected[0]]?.name;

      if (rejected.length === 1) {
        toast.error(
          translate('companyDocuments.upload.errorMessage', {
            fileName: rejectedFileName,
          }),
        );
       } else {
        toast.error(
          translate('companyDocuments.upload.errorMessagePlural', {
            count: rejected.length - 1,
            fileName: rejectedFileName,
          }),
        );
      }
    }

    setLoading(false);
    setCompletedCount(0);
    setTotalCount(0);
    if (files.length > 1) {
      toast.success(translate('companyDocuments.upload.successMessagePlural'));
    } else {
      toast.success(translate('companyDocuments.upload.successMessage'));
    }
    onComplete();
  };

  return {
    loading,
    uploadCompanyDocuments,
    uploadProgress: Math.round((completedCount / totalCount) * 100),
  };
};

export default useUploadCompanyDocuments;
